import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import {
  Notifications as NotificationIcon,
  MarkEmailRead as MarkReadIcon,
  Delete as DeleteIcon,
  Telegram as TelegramIcon,
  Settings as SettingsIcon,
  Send as SendIcon,
  NotificationImportant,
  Info,
  Warning,
  CampaignOutlined as BroadcastIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Link as LinkIcon,
  Refresh as RefreshIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  RemoveCircle as RemoveCircleIcon,
  NotificationsActive as NotificationActiveIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { 
  notificationService, 
  Notification, 
  TelegramConfig, 
  TelegramUser, 
  SendMessageData,
  MessageResult 
} from '../../services/notificationService';

const NotificationsTab: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [telegramConfig, setTelegramConfig] = useState<TelegramConfig>({ enabled: false });
  const [telegramUsers, setTelegramUsers] = useState<TelegramUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  
  // Telegram messaging states
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'targeted' | 'broadcast'>('targeted');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState<MessageResult | null>(null);
  
  // Dialog states
  const [telegramDialogOpen, setTelegramDialogOpen] = useState(false);
  const [testMessageDialogOpen, setTestMessageDialogOpen] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [notificationDialog, setNotificationDialog] = useState<{open: boolean, user: TelegramUser | null}>({open: false, user: null});
  const [notificationMessage, setNotificationMessage] = useState('');
  
  // Notification settings states
  const [notificationSettings, setNotificationSettings] = useState({
    level_1_increase: 5.0,
    level_1_decrease: -5.0,
    level_2_increase: 10.0,
    level_2_decrease: -10.0,
    level_3_increase: 20.0,
    level_3_decrease: -20.0
  });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [notificationsData, configData, telegramUsersData, settingsData] = await Promise.all([
        notificationService.getNotifications(),
        notificationService.getTelegramConfig(),
        notificationService.getTelegramUsers(),
        loadNotificationSettings(),
      ]);
      setNotifications(notificationsData);
      setTelegramConfig(configData);
      setTelegramUsers(telegramUsersData.users);
      if (settingsData) {
        setNotificationSettings(settingsData);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const loadNotificationSettings = async () => {
    try {
      const response = await fetch('/api/admin/notification-settings', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Error loading notification settings:', error);
    }
    return null;
  };

  const saveNotificationSettings = async () => {
    try {
      setSavingSettings(true);
      const response = await fetch('/api/admin/notification-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(notificationSettings),
      });
      
      if (response.ok) {
        toast.success('Notification settings saved successfully');
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save notification settings');
      console.error('Error saving notification settings:', error);
    } finally {
      setSavingSettings(false);
    }
  };

  const loadTelegramUsers = async () => {
    try {
      const response = await notificationService.getTelegramUsers();
      setTelegramUsers(response.users);
    } catch (error: any) {
      toast.error('Failed to load Telegram users');
      console.error('Error loading Telegram users:', error);
    }
  };

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      toast.success('Notification marked as read');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to mark as read');
    }
  };

  const handleDeleteNotification = async (notificationId: number) => {
    try {
      await notificationService.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Notification deleted');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete notification');
    }
  };

  const handleTelegramConfigSave = async (config: TelegramConfig) => {
    try {
      setSavingConfig(true);
      await notificationService.updateTelegramConfig(config);
      setTelegramConfig(config);
      toast.success('Telegram configuration saved successfully');
      setTelegramDialogOpen(false);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSendTestMessage = async () => {
    if (!testMessage.trim()) {
      toast.error('Please enter a test message');
      return;
    }

    try {
      setSendingTest(true);
      await notificationService.sendTelegramMessage(testMessage);
      toast.success('Test message sent successfully');
      setTestMessageDialogOpen(false);
      setTestMessage('');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to send test message');
    } finally {
      setSendingTest(false);
    }
  };

  // Telegram messaging functions
  const handleUserSelection = (chatId: string) => {
    setSelectedUsers(prev => {
      if (prev.includes(chatId)) {
        return prev.filter(id => id !== chatId);
      } else {
        return [...prev, chatId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === telegramUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(telegramUsers.map(user => user.chat_id));
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    if (messageType === 'targeted' && selectedUsers.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    try {
      setSending(true);
      let result: MessageResult;

      if (messageType === 'broadcast') {
        const broadcastResult = await notificationService.broadcastMessage(message);
        result = {
          sent: broadcastResult.sent_count,
          failed: 0,
          total: broadcastResult.sent_count
        };
      } else {
        const targetedResult = await notificationService.sendTargetedMessage({
          message,
          recipients: selectedUsers,
          type: 'targeted'
        });
        result = targetedResult.result || {
          sent: targetedResult.sent_count || 0,
          failed: 0,
          total: targetedResult.sent_count || 0
        };
      }

      setLastResult(result);
      toast.success(`Message sent: ${result.sent} delivered, ${result.failed} failed`);
      setMessage('');
      setSelectedUsers([]);
    } catch (error: any) {
      toast.error('Failed to send message');
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleDeleteTelegramUser = async (userId: string, username: string) => {
    if (!window.confirm(`Are you sure you want to delete "${username}"?`)) {
      return;
    }

    try {
      await notificationService.adminDeleteTelegramUser(userId);
      toast.success(`Deleted user: ${username}`);
      loadTelegramUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleLinkUser = async (userId: string) => {
    try {
      const result = await notificationService.linkTelegramUser(userId);
      toast.success(`Linked user: ${result.telegram_user.username}`);
      loadTelegramUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to link user');
    }
  };

  const handleRemoveAppTelegramLink = async (chatId: string, username: string) => {
    if (!window.confirm(`Are you sure you want to remove Telegram link from "${username}"?`)) {
      return;
    }

    try {
      const result = await notificationService.adminRemoveAppTelegramLink(chatId);
      toast.success(`Removed Telegram link from: ${result.updated_user.name}`);
      loadTelegramUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to remove link');
    }
  };

  const handleReloadChatIds = async () => {
    try {
      const result = await notificationService.reloadChatIds();
      toast.success(`Chat IDs reloaded: ${result.old_count} → ${result.new_count}`);
      console.log('Reloaded chat IDs:', result.chat_ids);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to reload chat IDs');
      console.error('Reload error:', error);
    }
  };

  const handleSendNotificationToUser = (user: TelegramUser) => {
    setNotificationDialog({open: true, user});
    setNotificationMessage('');
  };

  const handleSendNotification = async () => {
    if (!notificationDialog.user || !notificationMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      setSending(true);
      const targetedResult = await notificationService.sendTargetedMessage({
        message: notificationMessage,
        recipients: [notificationDialog.user.chat_id],
        type: 'targeted'
      });

      if (targetedResult.result && targetedResult.result.sent > 0) {
        toast.success(`Notification sent to ${notificationDialog.user.username}`);
        setNotificationDialog({open: false, user: null});
        setNotificationMessage('');
      } else {
        toast.error('Failed to send notification');
      }
    } catch (error: any) {
      toast.error('Failed to send notification');
      console.error('Error sending notification:', error);
    } finally {
      setSending(false);
    }
  };

  const getUserTypeChip = (user: TelegramUser) => {
    if (user.type === 'app_user') {
      return <Chip label="App User" color="primary" size="small" />;
    } else if (user.is_linked) {
      return <Chip label="Linked" color="success" size="small" />;
    } else {
      return <Chip label="Telegram Only" color="default" size="small" />;
    }
  };

  const getMessageTypeDescription = () => {
    switch (messageType) {
      case 'broadcast':
        return 'Send to all registered Telegram users';
      case 'targeted':
        return 'Send to selected users only';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Notification Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Overview Cards */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" component="div">
                  <TelegramIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Telegram Integration
                </Typography>
                <Chip 
                  label={telegramConfig.enabled ? 'Enabled' : 'Disabled'} 
                  color={telegramConfig.enabled ? 'success' : 'default'}
                />
              </Box>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                Configure Telegram bot for notifications and messaging
              </Typography>

              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={<SettingsIcon />}
                  onClick={() => setTelegramDialogOpen(true)}
                >
                  Configure
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={() => setTestMessageDialogOpen(true)}
                  disabled={!telegramConfig.enabled}
                >
                  Test Message
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="div" gutterBottom>
                <NotificationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Statistics
              </Typography>
              
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Total Notifications:</Typography>
                  <Chip label={notifications.length} color="primary" size="small" />
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Unread:</Typography>
                  <Chip 
                    label={notifications.filter(n => !n.is_read).length} 
                    color="warning" 
                    size="small" 
                  />
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Telegram Users:</Typography>
                  <Chip 
                    label={telegramUsers.length} 
                    color="info" 
                    size="small" 
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Telegram Messaging Section */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                <SendIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Telegram Messaging
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Compose Message
                      </Typography>
                      
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Message Type</InputLabel>
                        <Select
                          value={messageType}
                          onChange={(e) => setMessageType(e.target.value as 'targeted' | 'broadcast')}
                          label="Message Type"
                        >
                          <MenuItem value="targeted">
                            <Box display="flex" alignItems="center">
                              <PersonIcon sx={{ mr: 1 }} />
                              Targeted Message
                            </Box>
                          </MenuItem>
                          <MenuItem value="broadcast">
                            <Box display="flex" alignItems="center">
                              <BroadcastIcon sx={{ mr: 1 }} />
                              Broadcast Message
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>

                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        {getMessageTypeDescription()}
                      </Typography>

                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Enter your message here..."
                        margin="normal"
                      />

                      {messageType === 'targeted' && (
                        <Box mt={2}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="subtitle2">
                              Select Recipients ({selectedUsers.length}/{telegramUsers.length})
                            </Typography>
                            <Button size="small" onClick={handleSelectAll}>
                              {selectedUsers.length === telegramUsers.length ? 'Deselect All' : 'Select All'}
                            </Button>
                          </Box>
                          
                          <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                            <List dense>
                              {telegramUsers.map((user) => (
                                <ListItem key={user.id} button onClick={() => handleUserSelection(user.chat_id)}>
                                  <ListItemIcon>
                                    <Checkbox
                                      edge="start"
                                      checked={selectedUsers.includes(user.chat_id)}
                                      tabIndex={-1}
                                      disableRipple
                                    />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={user.username}
                                    secondary={`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.chat_id}
                                  />
                                  <ListItemSecondaryAction>
                                    {getUserTypeChip(user)}
                                  </ListItemSecondaryAction>
                                </ListItem>
                              ))}
                            </List>
                          </Paper>
                        </Box>
                      )}

                      <Box mt={3} display="flex" gap={2}>
                        <Button
                          variant="contained"
                          startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
                          onClick={handleSendMessage}
                          disabled={sending || !message.trim() || (messageType === 'targeted' && selectedUsers.length === 0)}
                          fullWidth
                        >
                          {sending ? 'Sending...' : `Send ${messageType === 'broadcast' ? 'Broadcast' : 'Message'}`}
                        </Button>
                      </Box>

                      {lastResult && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          Last message result: {lastResult.sent} sent, {lastResult.failed} failed out of {lastResult.total} total
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Quick Stats
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={2}>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2">Total Users:</Typography>
                          <Chip label={telegramUsers.length} color="primary" size="small" />
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2">App Users:</Typography>
                          <Chip 
                            label={telegramUsers.filter(u => u.type === 'app_user').length} 
                            color="success" 
                            size="small" 
                          />
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2">Telegram Only:</Typography>
                          <Chip 
                            label={telegramUsers.filter(u => u.type === 'telegram_user' && !u.is_linked).length} 
                            color="default" 
                            size="small" 
                          />
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2">Selected:</Typography>
                          <Chip 
                            label={selectedUsers.length} 
                            color="secondary" 
                            size="small" 
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* User Management Section */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                <GroupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Telegram User Management
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" gutterBottom>
                      Registered Telegram Users
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={loadTelegramUsers}
                      disabled={loading}
                    >
                      Refresh Users
                    </Button>
                  </Box>
                  
                  {telegramUsers.length === 0 ? (
                    <Box textAlign="center" py={4}>
                      <TelegramIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                      <Typography variant="h6" color="textSecondary">
                        No Telegram Users Registered
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Users can register by sending "/register [Name]" to your Telegram bot
                      </Typography>
                    </Box>
                  ) : (
                    <List>
                      {telegramUsers.map((user) => (
                        <React.Fragment key={user.id}>
                          <ListItem>
                            <ListItemIcon>
                              <Avatar sx={{ bgcolor: user.type === 'app_user' ? 'primary.main' : 'grey.500' }}>
                                {user.username.charAt(0).toUpperCase()}
                              </Avatar>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="subtitle1">{user.username}</Typography>
                                  {getUserTypeChip(user)}
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="body2" color="textSecondary">
                                    {user.first_name && user.last_name ? 
                                      `${user.first_name} ${user.last_name}` : 
                                      user.first_name || 'No name available'
                                    }
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    Chat ID: {user.chat_id}
                                  </Typography>
                                  {user.telegram_username && (
                                    <Typography variant="caption" color="textSecondary" display="block">
                                      @{user.telegram_username}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Box display="flex" gap={1}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<NotificationActiveIcon />}
                                  onClick={() => handleSendNotificationToUser(user)}
                                  sx={{ mr: 1 }}
                                >
                                  Notify
                                </Button>
                                {user.type === 'telegram_user' && !user.is_linked && (
                                  <Button
                                    size="small"
                                    startIcon={<LinkIcon />}
                                    onClick={() => handleLinkUser(user.id)}
                                  >
                                    Link to Account
                                  </Button>
                                )}
                              </Box>
                            </ListItemSecondaryAction>
                          </ListItem>
                          <Divider />
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Notification Settings Section */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Price Alert Settings
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Configure Price Change Notification Levels
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Set percentage thresholds for price change alerts. Only the highest level alert will be sent per stock per day.
                  </Typography>
                  
                  <Grid container spacing={3}>
                    {/* Level 1 Settings */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'warning.main' }}>
                        ⚠️ Level 1 Alerts (Default)
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="Increase Threshold (%)"
                            type="number"
                            value={notificationSettings.level_1_increase}
                            onChange={(e) => setNotificationSettings(prev => ({
                              ...prev,
                              level_1_increase: parseFloat(e.target.value)
                            }))}
                            inputProps={{ step: 0.1, min: 0 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="Decrease Threshold (%)"
                            type="number"
                            value={notificationSettings.level_1_decrease}
                            onChange={(e) => setNotificationSettings(prev => ({
                              ...prev,
                              level_1_decrease: parseFloat(e.target.value)
                            }))}
                            inputProps={{ step: 0.1, max: 0 }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Level 2 Settings */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'error.main' }}>
                        🚨 Level 2 Alerts (Warning)
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="Increase Threshold (%)"
                            type="number"
                            value={notificationSettings.level_2_increase}
                            onChange={(e) => setNotificationSettings(prev => ({
                              ...prev,
                              level_2_increase: parseFloat(e.target.value)
                            }))}
                            inputProps={{ step: 0.1, min: 0 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="Decrease Threshold (%)"
                            type="number"
                            value={notificationSettings.level_2_decrease}
                            onChange={(e) => setNotificationSettings(prev => ({
                              ...prev,
                              level_2_decrease: parseFloat(e.target.value)
                            }))}
                            inputProps={{ step: 0.1, max: 0 }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Level 3 Settings */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'error.dark' }}>
                        🔥 Level 3 Alerts (Critical)
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="Increase Threshold (%)"
                            type="number"
                            value={notificationSettings.level_3_increase}
                            onChange={(e) => setNotificationSettings(prev => ({
                              ...prev,
                              level_3_increase: parseFloat(e.target.value)
                            }))}
                            inputProps={{ step: 0.1, min: 0 }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="Decrease Threshold (%)"
                            type="number"
                            value={notificationSettings.level_3_decrease}
                            onChange={(e) => setNotificationSettings(prev => ({
                              ...prev,
                              level_3_decrease: parseFloat(e.target.value)
                            }))}
                            inputProps={{ step: 0.1, max: 0 }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Save Button */}
                    <Grid item xs={12}>
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          onClick={saveNotificationSettings}
                          disabled={savingSettings}
                          startIcon={savingSettings ? <CircularProgress size={20} /> : <SettingsIcon />}
                        >
                          {savingSettings ? 'Saving...' : 'Save Settings'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Recent Notifications */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Notifications
              </Typography>
              
              {notifications.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <NotificationIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No notifications yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Notifications will appear here when they are created
                  </Typography>
                </Box>
              ) : (
                <List>
                  {notifications.slice(0, 10).map((notification) => (
                    <React.Fragment key={notification.id}>
                      <ListItem>
                        <ListItemIcon>
                          {notification.type === 'SYSTEM' && <Info color="info" />}
                          {notification.type === 'PRICE_ALERT' && <Warning color="warning" />}
                          {notification.type === 'PORTFOLIO_UPDATE' && <NotificationIcon color="success" />}
                        </ListItemIcon>
                        <ListItemText
                          primary={notification.title}
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {notification.message}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(notification.created_at).toLocaleString()}
                              </Typography>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Box display="flex" gap={1}>
                            {!notification.is_read && (
                              <IconButton
                                size="small"
                                onClick={() => handleMarkAsRead(notification.id)}
                                title="Mark as read"
                              >
                                <MarkReadIcon />
                              </IconButton>
                            )}
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteNotification(notification.id)}
                              title="Delete"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Telegram Configuration Dialog */}
      <Dialog open={telegramDialogOpen} onClose={() => setTelegramDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Telegram Configuration</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={telegramConfig.enabled}
                  onChange={(e) => setTelegramConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                />
              }
              label="Enable Telegram Integration"
            />
            
            <TextField
              fullWidth
              label="Bot Token"
              value={telegramConfig.bot_token || ''}
              onChange={(e) => setTelegramConfig(prev => ({ ...prev, bot_token: e.target.value }))}
              margin="normal"
              type="password"
              helperText="Get this from @BotFather on Telegram"
            />
            
            <TextField
              fullWidth
              label="Chat ID"
              value={telegramConfig.chat_id || ''}
              onChange={(e) => setTelegramConfig(prev => ({ ...prev, chat_id: e.target.value }))}
              margin="normal"
              helperText="Your personal chat ID for notifications"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTelegramDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => handleTelegramConfigSave(telegramConfig)} 
            variant="contained"
            disabled={savingConfig}
          >
            {savingConfig ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Message Dialog */}
      <Dialog open={testMessageDialogOpen} onClose={() => setTestMessageDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Test Message</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Test Message"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={testMessage}
            onChange={(e) => setTestMessage(e.target.value)}
            placeholder="Enter a test message to send via Telegram..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestMessageDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSendTestMessage} 
            variant="contained"
            disabled={sendingTest || !testMessage.trim()}
          >
            {sendingTest ? 'Sending...' : 'Send Test'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Dialog */}
      <Dialog 
        open={notificationDialog.open} 
        onClose={() => setNotificationDialog({open: false, user: null})}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Send Notification to {notificationDialog.user?.username}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Notification Message"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={notificationMessage}
            onChange={(e) => setNotificationMessage(e.target.value)}
            placeholder="Enter your notification message..."
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setNotificationDialog({open: false, user: null})}
            disabled={sending}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSendNotification}
            variant="contained"
            disabled={sending || !notificationMessage.trim()}
            startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {sending ? 'Sending...' : 'Send Notification'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationsTab;